import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useSeo, fmtDate } from "@/lib/seo";

export default function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    api.get(`/blog/${slug}`).then((r) => setData(r.data)).catch(() => setData("error"));
  }, [slug]);

  const post = data && data !== "error" ? data.post : null;

  const jsonLd = useMemo(() => post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: [post.image],
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: post.author || "Furn Angling" },
    publisher: { "@type": "Organization", name: "Furn Angling" },
    description: post.meta_description || post.excerpt,
    articleSection: post.category,
    keywords: (post.tags || []).join(", "),
  } : null, [post]);

  useSeo({
    title: post ? (post.meta_title || `${post.title} | Furn Angling`) : "Blog | Furn Angling",
    description: post ? (post.meta_description || post.excerpt) : "",
    image: post ? post.image : undefined,
    type: "article",
    jsonLd,
  });

  if (data === "error") return <div className="container-furn py-24 text-center"><h1 className="font-display text-3xl">Post not found</h1><Link to="/blog" className="mt-4 inline-block text-accent">Back to blog</Link></div>;
  if (!post) return <div className="container-furn py-24"><div className="h-96 animate-pulse bg-secondary" /></div>;

  return (
    <article className="pb-12">
      <div className="relative h-[45vh] min-h-[320px] w-full overflow-hidden">
        <img src={post.image} alt={post.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="container-furn relative flex h-full flex-col justify-end pb-10">
          <nav className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-accent">Home</Link><ChevronRight className="h-3 w-3" />
            <Link to="/blog" className="hover:text-accent">Blog</Link><ChevronRight className="h-3 w-3" />
            <span className="text-foreground">{post.category}</span>
          </nav>
          <span className="overline">{post.category}</span>
          <h1 className="mt-2 max-w-3xl font-display text-4xl font-bold uppercase leading-tight md:text-5xl">{post.title}</h1>
          <p className="mt-3 text-sm text-muted-foreground">By {post.author} · {fmtDate(post.date)} · {post.read} read</p>
        </div>
      </div>

      <div className="container-furn mt-10 max-w-3xl">
        <Link to="/blog" className="mb-6 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-accent"><ArrowLeft className="h-4 w-4" /> All guides</Link>
        <div className="space-y-5 text-lg leading-relaxed text-foreground/85">
          {post.body.map((para, i) => <p key={i}>{para}</p>)}
        </div>
        {post.tags && post.tags.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2 border-t border-border pt-6">
            {post.tags.map((t) => <span key={t} className="border border-border px-3 py-1 text-xs text-muted-foreground">#{t}</span>)}
          </div>
        )}
      </div>

      {data.related && data.related.length > 0 && (
        <div className="container-furn mt-16">
          <h2 className="mb-6 font-display text-2xl font-semibold uppercase">More guides</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {data.related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group border border-border bg-card transition-colors hover:border-primary">
                <div className="aspect-[4/3] overflow-hidden bg-secondary"><img src={p.image} alt={p.title} className="zoom-img h-full w-full object-cover" /></div>
                <div className="p-5"><span className="overline">{p.category}</span><h3 className="mt-2 font-display text-base font-medium group-hover:text-accent transition-colors line-clamp-2">{p.title}</h3></div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
